import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, Star } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { CATEGORY_LABELS } from '@/components/showcase/RigCard';

function RatingCell({ value }) {
  const v = Number(value || 0);
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-white font-bold text-sm">{v.toFixed(1)}</span>
      <Star className="w-3 h-3 text-primary fill-primary" />
    </div>
  );
}

export default function ShowcaseCompareTable() {
  const { data: rigs = [], isLoading } = useQuery({
    queryKey: ['showcase-rigs-all'],
    queryFn: () => base44.entities.ShowcaseRig.list('sort_order', 100),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (rigs.length === 0) {
    return <p className="text-white/40 text-sm text-center py-12">No simulators to compare yet.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left p-4 text-white/40 text-[10px] font-bold tracking-widest">RIG</th>
            <th className="text-left p-4 text-white/40 text-[10px] font-bold tracking-widest">CATEGORY</th>
            <th className="text-left p-4 text-white/40 text-[10px] font-bold tracking-widest">REALISM</th>
            <th className="text-left p-4 text-white/40 text-[10px] font-bold tracking-widest">IMMERSION</th>
            <th className="text-left p-4 text-white/40 text-[10px] font-bold tracking-widest">BEGINNER</th>
            <th className="text-left p-4 text-white/40 text-[10px] font-bold tracking-widest">COMPETITION</th>
            <th className="text-left p-4 text-white/40 text-[10px] font-bold tracking-widest">HARDWARE</th>
            <th className="text-left p-4 text-white/40 text-[10px] font-bold tracking-widest">OVERALL</th>
            <th className="p-4"></th>
          </tr>
        </thead>
        <tbody>
          {rigs.map((rig) => (
            <tr key={rig.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
              <td className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0d0d0f] border border-white/5 overflow-hidden shrink-0">
                    {rig.hero_image_url ? (
                      <img src={rig.hero_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full carbon-bg" />
                    )}
                  </div>
                  <span className="text-white font-bold text-sm">{rig.name}</span>
                </div>
              </td>
              <td className="p-4">
                <span className="text-white/50 text-xs font-bold">{CATEGORY_LABELS[rig.category] || rig.category}</span>
              </td>
              <td className="p-4"><RatingCell value={rig.rating_realism} /></td>
              <td className="p-4"><RatingCell value={rig.rating_immersion} /></td>
              <td className="p-4"><RatingCell value={rig.rating_beginner_friendly} /></td>
              <td className="p-4"><RatingCell value={rig.rating_competitive} /></td>
              <td className="p-4"><RatingCell value={rig.rating_hardware} /></td>
              <td className="p-4"><RatingCell value={rig.overall_rating} /></td>
              <td className="p-4">
                <Link
                  to={`/simulators/${rig.slug}`}
                  className="text-primary text-xs font-bold tracking-widest hover:underline whitespace-nowrap"
                >
                  VIEW →
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}